/**
 * Reference-based denoise — classical spectral subtraction.
 *
 * The user marks a silent region (only noise, no voice). We learn the noise
 * magnitude spectrum from that region and subtract it from every frame of the
 * full signal. This handles cases the generic AI / afftdn don't — equipment-
 * specific hum patterns, fan profiles, environmental tones the model wasn't
 * trained on.
 *
 * Algorithm: STFT → per-frame |X(f)| − α·|N(f)| with floor β·|X(f)| → iSTFT.
 * Window 1024 samples, hop 256 (75% overlap), Hann window.
 *
 * Public API:
 *   RefDenoiseProcessor.process(audioBuffer, noiseStartSec, noiseEndSec, opts)
 *     returns AudioBuffer with same sampleRate / channel count.
 *
 *   // A recording too long to hold decoded (2026-10-14): learn the noise once,
 *   // then feed the recording in blocks of any size. The answer is the same
 *   // samples process() gives for the whole buffer — the whole-buffer path IS
 *   // the stream, fed in pieces.
 *   var profile = RefDenoiseProcessor.learn(bufferWithTheNoise, startSec, endSec);
 *   var stream  = RefDenoiseProcessor.createStream(profile, { alpha: 2.0, beta: 0.05 });
 *   out  = stream.push([Float32Array per channel]);   // the samples that are final
 *   tail = stream.flush();                             // the rest; in total = samples fed
 *
 * Why the stream exists: «clean by a noise sample» is offered when the normal
 * run came out weak, and on a recording past the decode budget it answered
 * with a bare «Ошибка» — it needed the whole recording decoded (30 days: 8
 * sessions hit that against 34 runs that got through; support chat 686, a
 * 48-minute call, right after a 30-minute run).
 */
window.RefDenoiseProcessor = (function() {
    var FFT_SIZE = 1024;
    var HOP = 256;

    // Pre-computed Hann window (length FFT_SIZE)
    var _hann = (function() {
        var w = new Float32Array(FFT_SIZE);
        for (var i = 0; i < FFT_SIZE; i++) {
            w[i] = 0.5 - 0.5 * Math.cos(2 * Math.PI * i / (FFT_SIZE - 1));
        }
        return w;
    })();

    // Radix-2 in-place FFT. re/im are Float32Array of length n (power of 2).
    function fft(re, im) {
        var n = re.length;
        // Bit-reversal permutation
        for (var i = 1, j = 0; i < n; i++) {
            var bit = n >> 1;
            for (; j & bit; bit >>= 1) j ^= bit;
            j ^= bit;
            if (i < j) {
                var tr = re[i]; re[i] = re[j]; re[j] = tr;
                var ti = im[i]; im[i] = im[j]; im[j] = ti;
            }
        }
        for (var size = 2; size <= n; size <<= 1) {
            var half = size >> 1;
            var angle = -2 * Math.PI / size;
            var wRe = Math.cos(angle), wIm = Math.sin(angle);
            for (var k = 0; k < n; k += size) {
                var curRe = 1, curIm = 0;
                for (var m = 0; m < half; m++) {
                    var oRe = re[k + m + half] * curRe - im[k + m + half] * curIm;
                    var oIm = re[k + m + half] * curIm + im[k + m + half] * curRe;
                    var eRe = re[k + m], eIm = im[k + m];
                    re[k + m] = eRe + oRe;
                    im[k + m] = eIm + oIm;
                    re[k + m + half] = eRe - oRe;
                    im[k + m + half] = eIm - oIm;
                    var tw = curRe * wRe - curIm * wIm;
                    curIm = curRe * wIm + curIm * wRe;
                    curRe = tw;
                }
            }
        }
    }

    function ifft(re, im) {
        var n = re.length;
        for (var i = 0; i < n; i++) im[i] = -im[i];
        fft(re, im);
        for (var k = 0; k < n; k++) { re[k] /= n; im[k] = -im[k] / n; }
    }

    // Compute averaged magnitude spectrum over [startSample, endSample) of mono data.
    function learnNoise(data, startSample, endSample) {
        var nBins = FFT_SIZE / 2 + 1;
        var noiseMag = new Float32Array(nBins);
        var frames = 0;
        var re = new Float32Array(FFT_SIZE);
        var im = new Float32Array(FFT_SIZE);
        for (var off = startSample; off + FFT_SIZE <= endSample; off += HOP) {
            for (var i = 0; i < FFT_SIZE; i++) {
                re[i] = (data[off + i] || 0) * _hann[i];
                im[i] = 0;
            }
            fft(re, im);
            for (var k = 0; k < nBins; k++) {
                var mag = Math.sqrt(re[k] * re[k] + im[k] * im[k]);
                noiseMag[k] += mag;
            }
            frames++;
        }
        if (frames === 0) {
            // Fallback: tiny region, single frame zero-padded
            var off2 = startSample;
            var len = Math.min(FFT_SIZE, endSample - startSample);
            for (var i = 0; i < FFT_SIZE; i++) {
                re[i] = (i < len ? (data[off2 + i] || 0) : 0) * _hann[i];
                im[i] = 0;
            }
            fft(re, im);
            for (var k = 0; k < nBins; k++) {
                noiseMag[k] = Math.sqrt(re[k] * re[k] + im[k] * im[k]);
            }
            frames = 1;
        }
        for (var k = 0; k < nBins; k++) noiseMag[k] /= frames;
        return noiseMag;
    }

    /* One channel as a STREAM: frames of FFT_SIZE every HOP, spectral
       subtraction per bin, overlap-add normalised by the summed window². After
       each frame the first HOP samples under the window are final — no later
       frame reaches them — and are handed out.
       alpha = over-subtraction factor (1.5–3.0; higher = more aggressive)
       beta  = spectral floor (0.01–0.1; prevents musical noise)

       The END is processed too: what is left after the last full frame is
       framed zero-padded. (The whole-buffer loop that stood here stopped at
       the last full frame and wrote up to 1023 samples of SILENCE at the end
       of every file it made.) */
    function ChannelStream(noiseMag, alpha, beta) {
        var nBins = FFT_SIZE / 2 + 1;
        var re = new Float32Array(FFT_SIZE), im = new Float32Array(FFT_SIZE);
        var pend = new Float32Array(FFT_SIZE * 2), pendLen = 0;                  // input not yet consumed
        var acc = new Float32Array(FFT_SIZE), cnt = new Float32Array(FFT_SIZE); // what the window is over

        function frame(x, off) {
            for (var i = 0; i < FFT_SIZE; i++) { re[i] = x[off + i] * _hann[i]; im[i] = 0; }
            fft(re, im);
            for (var k = 0; k < nBins; k++) {
                var sigRe = re[k], sigIm = im[k];
                var mag = Math.sqrt(sigRe * sigRe + sigIm * sigIm);
                var sub = mag - alpha * noiseMag[k];
                if (sub < beta * mag) sub = beta * mag;
                var gain = mag > 1e-12 ? sub / mag : 0;
                re[k] = sigRe * gain;
                im[k] = sigIm * gain;
                // Mirror the conjugate bins (k > nBins-1) for proper iFFT
                if (k > 0 && k < FFT_SIZE - k) {
                    re[FFT_SIZE - k] = re[k];
                    im[FFT_SIZE - k] = -im[k];
                }
            }
            ifft(re, im);
            for (var j = 0; j < FFT_SIZE; j++) { acc[j] += re[j] * _hann[j]; cnt[j] += _hann[j] * _hann[j]; }
        }
        /* Hand out the first `n` (≤ HOP) samples under the window, slide by HOP. */
        function emit(out, at, n) {
            for (var i = 0; i < n; i++) out[at + i] = cnt[i] > 1e-6 ? acc[i] / cnt[i] : 0;
            acc.copyWithin(0, HOP); acc.fill(0, FFT_SIZE - HOP);
            cnt.copyWithin(0, HOP); cnt.fill(0, FFT_SIZE - HOP);
        }
        return {
            push: function (x) {
                if (pendLen + x.length > pend.length) {
                    var grown = new Float32Array(pendLen + x.length + FFT_SIZE);
                    grown.set(pend.subarray(0, pendLen)); pend = grown;
                }
                pend.set(x, pendLen); pendLen += x.length;
                var frames = pendLen >= FFT_SIZE ? Math.floor((pendLen - FFT_SIZE) / HOP) + 1 : 0;
                var out = new Float32Array(frames * HOP);
                for (var f = 0; f < frames; f++) { frame(pend, f * HOP); emit(out, f * HOP, HOP); }
                if (frames) { pend.copyWithin(0, frames * HOP, pendLen); pendLen -= frames * HOP; }
                return out;
            },
            /* Every sample fed and not yet handed out is still in `pend` — it
               is the input of the frames that did not fill. */
            flush: function () {
                var owed = pendLen, out = new Float32Array(owed), at = 0;
                var pad = new Float32Array(FFT_SIZE);
                while (at < owed) {
                    pad.fill(0);
                    pad.set(pend.subarray(at, Math.min(owed, at + FFT_SIZE)));
                    frame(pad, 0);
                    var n = Math.min(HOP, owed - at);
                    emit(out, at, n);
                    at += n;
                }
                pendLen = 0;
                return out;
            }
        };
    }

    // Process one mono channel — returns a new Float32Array (same length).
    function processChannel(data, noiseMag, alpha, beta, onProgress) {
        var n = data.length, out = new Float32Array(n), at = 0;
        var st = ChannelStream(noiseMag, alpha, beta);
        var STEP = HOP * 512;                                   // ~2.7 s at 48 kHz
        for (var off = 0; off < n; off += STEP) {
            var got = st.push(data.subarray(off, Math.min(n, off + STEP)));
            out.set(got, at); at += got.length;
            if (onProgress) onProgress(Math.min(1, (off + STEP) / n));
        }
        out.set(st.flush(), at);
        return out;
    }

    /* The noise of a marked region, per channel. `buffer` — anything with
       sampleRate / numberOfChannels / length / getChannelData. */
    function learn(buffer, noiseStartSec, noiseEndSec) {
        var sr = buffer.sampleRate, n = buffer.length;
        var a = Math.max(0, Math.floor(noiseStartSec * sr)), b = Math.min(n, Math.floor(noiseEndSec * sr));
        if (b - a < FFT_SIZE) {
            throw new Error('Reference region too short (need ≥ ' + (FFT_SIZE / sr).toFixed(2) + ' s)');
        }
        var mags = [];
        for (var c = 0; c < buffer.numberOfChannels; c++) mags.push(learnNoise(buffer.getChannelData(c), a, b));
        return { sampleRate: sr, channels: mags.length, mags: mags };
    }

    /* The recording in blocks: push([one Float32Array per channel]) hands back
       the samples that are final, flush() the rest. Feed it at the sample rate
       and channel count the profile was learnt at. */
    function createStream(profile, opts) {
        opts = opts || {};
        var alpha = opts.alpha != null ? opts.alpha : 2.0, beta = opts.beta != null ? opts.beta : 0.05;
        var chans = profile.mags.map(function (m) { return ChannelStream(m, alpha, beta); });
        return {
            sampleRate: profile.sampleRate, channels: chans.length,
            push: function (block) { return chans.map(function (st, c) { return st.push(block[Math.min(c, block.length - 1)]); }); },
            flush: function () { return chans.map(function (st) { return st.flush(); }); }
        };
    }

    return {
        // audioBuffer: input AudioBuffer (any sampleRate, any channels)
        // noiseStart/EndSec: silent region in seconds
        // opts: { alpha?: 2.0, beta?: 0.05, onProgress?: (0..1) }
        // Returns AudioBuffer in the same sampleRate / channel count.
        process: async function(audioBuffer, noiseStartSec, noiseEndSec, opts) {
            opts = opts || {};
            var alpha = opts.alpha != null ? opts.alpha : 2.0;
            var beta = opts.beta != null ? opts.beta : 0.05;
            var onProgress = opts.onProgress || function(){};

            var sr = audioBuffer.sampleRate;
            var nCh = audioBuffer.numberOfChannels;
            var nSamples = audioBuffer.length;
            var noiseStart = Math.max(0, Math.floor(noiseStartSec * sr));
            var noiseEnd = Math.min(nSamples, Math.floor(noiseEndSec * sr));
            if (noiseEnd - noiseStart < FFT_SIZE) {
                throw new Error('Reference region too short (need ≥ ' + (FFT_SIZE / sr).toFixed(2) + ' s)');
            }

            // Prefer a fresh OfflineAudioContext to construct the output buffer
            var off = new OfflineAudioContext(nCh, nSamples, sr);
            var outBuf = off.createBuffer(nCh, nSamples, sr);

            var profile = learn(audioBuffer, noiseStartSec, noiseEndSec);
            for (var c = 0; c < nCh; c++) {
                var inData = audioBuffer.getChannelData(c);
                var noiseMag = profile.mags[c];
                var processed = processChannel(inData, noiseMag, alpha, beta, function(p) {
                    onProgress((c + p) / nCh);
                });
                outBuf.copyToChannel(processed, c);
                // Yield to UI between channels
                await new Promise(function(r) { setTimeout(r, 0); });
            }
            onProgress(1);
            return outBuf;
        },

        learn: learn,
        createStream: createStream,
        FFT_SIZE: FFT_SIZE,

        // Encode AudioBuffer to WAV Blob (16-bit PCM, little-endian).
        toWavBlob: function(audioBuffer) {
            var nCh = audioBuffer.numberOfChannels;
            var nSamples = audioBuffer.length;
            var sr = audioBuffer.sampleRate;
            var bytesPerSample = 2;
            var blockAlign = nCh * bytesPerSample;
            var byteRate = sr * blockAlign;
            var dataLen = nSamples * blockAlign;
            var buf = new ArrayBuffer(44 + dataLen);
            var view = new DataView(buf);
            function w(o, s) { for (var i = 0; i < s.length; i++) view.setUint8(o + i, s.charCodeAt(i)); }
            w(0, 'RIFF'); view.setUint32(4, 36 + dataLen, true);
            w(8, 'WAVE'); w(12, 'fmt '); view.setUint32(16, 16, true);
            view.setUint16(20, 1, true); view.setUint16(22, nCh, true);
            view.setUint32(24, sr, true); view.setUint32(28, byteRate, true);
            view.setUint16(32, blockAlign, true); view.setUint16(34, bytesPerSample * 8, true);
            w(36, 'data'); view.setUint32(40, dataLen, true);
            // Interleave channels
            var offset = 44;
            var chs = [];
            for (var c = 0; c < nCh; c++) chs.push(audioBuffer.getChannelData(c));
            for (var s = 0; s < nSamples; s++) {
                for (var c = 0; c < nCh; c++) {
                    var v = Math.max(-1, Math.min(1, chs[c][s]));
                    view.setInt16(offset, v < 0 ? v * 0x8000 : v * 0x7FFF, true);
                    offset += 2;
                }
            }
            return new Blob([buf], { type: 'audio/wav' });
        }
    };
})();
